// built-from: /activity-contract-pool v1 impl:activity-contract-pool role:implementation
// Feature 085: the Process Blueprint layout — pure geometry for the one
// canvas. The step ribbon is the temporal axis (the layered fork rule:
// follows advances the trunk, an explicit trigger branches from the point
// the story had reached and stacks); the domain overlay's entries place in
// their swimlane, aligned under the step columns they bind to, spanning
// where they cover several. No rendering here — the canvas component
// consumes this verbatim, and tests pin the geometry directly.

import type { StepNode } from './stepFlow';

export type DomainStatus = 'assumed' | 'confirmed';

export interface DomainOverlayEntry {
  id: string;
  label: string;
  status: DomainStatus;
  steps: number[];
}

export interface DomainOverlay {
  actors: DomainOverlayEntry[];
  contexts: DomainOverlayEntry[];
  systems: DomainOverlayEntry[];
}

export function emptyDomainOverlay(): DomainOverlay {
  return { actors: [], contexts: [], systems: [] };
}

export type BandKind = 'actors' | 'contexts' | 'systems';

export interface BlueprintChip {
  stepId: number;
  label: string;
  trigger: StepNode['trigger'];
  column: number;
  row: number;
  childCount: number;
}

export interface BlueprintCard {
  id: string;
  label: string;
  status: DomainStatus;
  columnStart: number;
  columnEnd: number;
  row: number;
}

export interface BlueprintBand {
  kind: BandKind;
  cards: BlueprintCard[];
  rowCount: number;
}

export interface BlueprintLayout {
  chips: BlueprintChip[];
  columnCount: number;
  chipRowCount: number;
  bands: BlueprintBand[];
  warnings: string[];
}

const BAND_KINDS: ReadonlyArray<BandKind> = ['actors', 'contexts', 'systems'];

// The ribbon is PURELY TEMPORAL: sibling order is column order, walked
// depth-first so a sub-process's steps sit between their parent and its
// next sibling — the owner's alignment rule ("the step should align
// temporally with the cards below it"). A triggered step is sequence with a
// cause, not automatically an alternative; compact alternative-stacking
// waits until alternatives have a real representation (the spine's curated
// groups or a policy overlay), because inferring them from trigger presence
// mis-stacks ordinary caused sequence (085 design decision).
// Exported for the 087 top card layer, which shares this temporal column
// assignment with the domain projection so layer and canvas stay aligned.
export function ribbonChips(steps: StepNode[]): { chips: BlueprintChip[]; columnCount: number; chipRowCount: number } {
  const chips: BlueprintChip[] = [];

  const walk = (node: StepNode) => {
    chips.push({
      stepId: node.id,
      label: node.label,
      trigger: node.trigger,
      column: chips.length,
      row: 0,
      childCount: node.steps.length,
    });
    node.steps.forEach(walk);
  };
  steps.forEach(walk);

  return {
    chips,
    columnCount: chips.length,
    chipRowCount: chips.length === 0 ? 0 : 1,
  };
}

// Greedy interval stacking: sort by start column, place each card on the
// first row whose previous occupant ends before this one starts.
function stackCards(cards: Omit<BlueprintCard, 'row'>[]): BlueprintCard[] {
  const sorted = [...cards].sort((a, b) => a.columnStart - b.columnStart || a.columnEnd - b.columnEnd);
  const rowEnds: number[] = [];
  return sorted.map((card) => {
    let row = rowEnds.findIndex((end) => end < card.columnStart);
    if (row === -1) {
      row = rowEnds.length;
      rowEnds.push(card.columnEnd);
    } else {
      rowEnds[row] = card.columnEnd;
    }
    return { ...card, row };
  });
}

export function blueprintLayout(steps: StepNode[], overlay: DomainOverlay): BlueprintLayout {
  const { chips, columnCount, chipRowCount } = ribbonChips(steps);
  const columnByStep = new Map(chips.map((chip) => [chip.stepId, chip.column]));
  const warnings: string[] = [];

  const bands: BlueprintBand[] = BAND_KINDS.map((kind) => {
    const entries = Array.isArray(overlay?.[kind]) ? overlay[kind] : [];
    const placeable: Omit<BlueprintCard, 'row'>[] = [];
    for (const entry of entries) {
      const columns = (Array.isArray(entry.steps) ? entry.steps : [])
        .map((stepId) => columnByStep.get(stepId))
        .filter((column): column is number => column !== undefined);
      if (columns.length === 0) {
        warnings.push(`${kind}: '${entry.label}' binds no known step — not placed`);
        continue;
      }
      placeable.push({
        id: entry.id,
        label: entry.label,
        status: entry.status === 'confirmed' ? 'confirmed' : 'assumed',
        columnStart: Math.min(...columns),
        columnEnd: Math.max(...columns),
      });
    }
    const cards = stackCards(placeable);
    const rowCount = cards.length === 0 ? 0 : Math.max(...cards.map((card) => card.row)) + 1;
    return { kind, cards, rowCount };
  });

  return { chips, columnCount, chipRowCount, bands, warnings };
}
